import React, { useState, useEffect } from "react";
import {
  Grid2,
  Typography,
  Button,
  Box,
  Paper,
  IconButton,
  Snackbar,
  Alert
} from "@mui/material";
import { usePermission } from "../authentication/permission-providor/UserPermission";
import { useDispatch, useSelector } from "react-redux";
import { getWorkspaceByRole } from "../../actions/workspaceAction";
import AddApp from "./AddApp";
import { DataGrid } from "@mui/x-data-grid";
import AddButton from "../../themes/overrides/AddButton";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchField from "../../themes/overrides/SearchField";
import EditIcon from "../../components/icons/EditIcon";
import RemoveIcon from "../../components/icons/RemoveIcon";
import MediaIcon from "../../assets/images/brush.svg"
import LoadingOverlay from "../../components/LoadingOverlay";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { getApps, installAppToWorkspace, removeApp } from "../../actions/appStudioAction";
import AppInstallModal from "../../components/AppInstallModal";
import InstallingIcon from '@mui/icons-material/InstallDesktop';

const AppStudio = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteApp, setSelectedDeleteApp] = useState(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null)

  const [installModalOpen, setInstallModalOpen] = useState(false);
  const [selectedInstallApp, setSelectedInstallApp] = useState(null);

  const workspaceId = useSelector((state) => state.workspace.workspaceID) || localStorage.getItem("workspaceID");
  const userId = localStorage.getItem('userId');
  const apps = useSelector((state) => state.apps.apps);
  const uploadSuccess = useSelector((state) => state.apps.success);
  const adminWorkspaces = useSelector((state) => state.workspace.workspaceByRole?.data);

  const [shouldRefresh, setShouldRefresh] = useState(true);
  const [reFetchWorkspaceByRole, setReFetchWorkspaceByRole] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [checkboxState, setCheckboxState] = useState({
    // sideBar: { checked: false, subCheckboxes: [] },
    textField: { checked: false, subCheckboxes: ['object'] },
  });

  const handleMainCheckboxChange = (name) => (event) => {
    setCheckboxState((prevState) => ({
      ...prevState,
      [name]: {
        ...prevState[name],
        checked: event.target.checked,
      },
    }));
  };

  const handleSubCheckboxChange = (mainName, subName) => (event) => {
    setCheckboxState((prevState) => {
      const subCheckboxes = prevState[mainName].subCheckboxes.includes(subName)
        ? prevState[mainName].subCheckboxes.filter((sub) => sub !== subName)
        : [...prevState[mainName].subCheckboxes, subName];
      return {
        ...prevState,
        [mainName]: {
          ...prevState[mainName],
          subCheckboxes,
        },
      };
    });
  };

  useEffect(() => {
    if (reFetchWorkspaceByRole) {
      dispatch(getWorkspaceByRole('admin'))
      setReFetchWorkspaceByRole(false)
    }
  }, [dispatch, reFetchWorkspaceByRole])

  const openAddMediaModal = () => {
    setModalOpen(true);
    setEditMode(false);
    setSelectedApp(null);
  };

  useEffect(() => {
    if (shouldRefresh || uploadSuccess) {
      setLoading(true);
      Promise.all([
        dispatch(getApps())
      ]).finally(() => setLoading(false));

      setShouldRefresh(false);
    }
  }, [shouldRefresh, uploadSuccess, workspaceId, dispatch]);

  const handleWorkspaceChange = (event) => {
    setSelectedWorkspace(event.target.value);
  };


  const handleEdit = (id) => {
    const appToEdit = apps.find((app) => app._id === id);

    if (appToEdit) {
      setSelectedApp(appToEdit);
      setEditMode(true);
      setModalOpen(true);
    }
  };

  const handleDelete = (appId) => {
    setSelectedDeleteApp(appId);
    setDeleteModalOpen(true);
  };

  const handleInstall = (appId) => {
    setSelectedInstallApp(appId);
    setInstallModalOpen(true);
  };

  const installApp = async () => {
    const success = await dispatch(installAppToWorkspace(selectedInstallApp, selectedWorkspace));
    if (success) {
      setShouldRefresh(true);
      setReFetchWorkspaceByRole(true);
      showSnackbar('App installed to workspace successfully!', 'success')
    }
    setInstallModalOpen(false);
    setSelectedInstallApp(null);
    setSelectedWorkspace(null);
  };

  const handleConfirmDelete = async () => {
    const success = await dispatch(removeApp(selectedDeleteApp));
    if (success) {
      setShouldRefresh(true);
      showSnackbar('App deleted successfully!', 'success')
    }
    setDeleteModalOpen(false);
    setSelectedDeleteApp(null);
  };

  const handleSubmitSuccess = () => {
    showSnackbar('App updated successfully!', 'success')
    setModalOpen(false);
    setShouldRefresh(true);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const columns = [
    { field: "id", headerName: "id", width: 50 },
    { field: "appName", headerName: "App Name", flex: 1 },
    { field: "url", headerName: "URL", flex: 1 },
    {
      field: "logo",
      headerName: "Logo",
      flex: 1,
      renderCell: (params) => (
        <img
          src={params.value}
          alt={`App ${params.row.id}`}
          style={{ width: '100%', height: 'auto' }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            onClick={() => handleEdit(params.row._id)}
            aria-label="edit"

          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => handleDelete(params.row._id)}
            aria-label="delete"

          >
            <RemoveIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => handleInstall(params.row._id)}
            aria-label="install"

          >
            <InstallingIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const rows = apps?.map((apps, index) => ({
    id: index,
    ...apps,
  }));

  const filteredRows = apps?.filter(
    (app) =>
      app.appName?.toLowerCase().includes(searchTerm)
  )
    .map((app, index) => ({
      id: index + 1,
      ...app,
    }));



  return (
    loading ? <LoadingOverlay /> :
      <>
        <Grid2 container spacing={2} justifyContent="space-between" alignItems="center">
          <Grid2 container size={12} display="flex" justifyContent="space-between">
            <Grid2 size={{ xs: 12, md: 8 }} display='flex' alignItems="flex-start" sx={{ marginLeft: { xs: 4, md: 0 } }}>
              <Box component="img" sx={{ width: { xs: 32, md: 40 }, marginRight: { xs: 2, md: 4 } }} src={MediaIcon} />
              <Typography variant={"h1"}>Apps</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }} offset={{ xs: 4, md: 0 }} display="flex" justifyContent="flex-end">
              <AddButton
                label="Add App"
                sx={{ mr: 1 }}
                onClick={openAddMediaModal}

              />
            </Grid2>
          </Grid2>

          <Grid2 container spacing={2} alignItems="center">
            <Grid2 size={12} >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexGrow: 1
                }}>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <SearchField
                    placeholder="Search App"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearch}
                    sx={{ flex: 1 }}
                  />
                </Grid2>
              </Box>
            </Grid2>
            <Grid2 size={4}></Grid2>
            <Grid2 size={12}>
              <Paper
                sx={{ overflowX: 'auto' }}
              >
                <Box sx={{ width: "100%", minWidth: { xs: 600, md: '100%' }, minHeight: 100, alignContent: "center" }}>
                  <DataGrid
                    disableRowSelectionOnClick
                    rows={filteredRows}
                    columns={columns}
                    disableColumnSelector
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 15,
                        },
                      },
                    }}
                    sx={{
                      '& .MuiDataGrid-cell:focus': {
                        outline: 'none'
                      },
                    }}
                    pageSizeOptions={[15]}
                  />
                </Box>

              </Paper>
            </Grid2>
          </Grid2>
          <AddApp
            isOpen={modalOpen}
            onClose={() => {
              setModalOpen(false);
            }}
            onSubmitSuccess={handleSubmitSuccess}
            editMode={editMode}
            app={selectedApp}
          />
        </Grid2>

        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          text="Are you sure you want to delete this app?"
          onConfirm={handleConfirmDelete}
          deleteButtonLabel="Delete"
        />

        <AppInstallModal
          open={installModalOpen}
          onClose={() => { setInstallModalOpen(false); setSelectedWorkspace(''); }}
          text="Are you sure you want to install this app?"
          onConfirm={installApp}
          deleteButtonLabel="Install"
          workspaces={adminWorkspaces}
          selectedWorkspace={selectedWorkspace}
          onWorkspaceChange={handleWorkspaceChange}
          selectedAppId={selectedInstallApp}
        />

        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >{snackbarMessage}</Alert>
        </Snackbar>
      </>
  );
};

export default AppStudio;
